/**
 * The demo data that is still left in the CardDetailHero.
 *
 * `card` and `limits` used to live here too; EX-40595 wired both to the real
 * Card Management client (`src/queries/physicalCardManagement/`) and the credit
 * card detail endpoint, so only the two groups with no live counterpart remain:
 *
 * - `billing` — the billing-address endpoint is not built yet.
 * - `credentials` — PAN/CVC have no contract counterpart *by design*; the public
 *   API never returns them and a real reveal would come from a separate vault
 *   path, not from any endpoint in the Card Management contract.
 *
 * The grouping is deliberate and survives the wiring: `card`, `limits`, and
 * `billing` correspond to *independently* fetched endpoints. Regions read only
 * the group they need, so a limits failure cannot blank the identity row.
 *
 * Money is always minor units plus an ISO-4217 currency, never a float —
 * conversion to a display amount happens at the formatter call site.
 */

/** Contract enum. LOCKED is issuer-imposed and is NOT a voluntary FROZEN. */
export type PhysicalCardManagementStatus =
  | 'PENDING_ISSUANCE'
  | 'ACTIVE'
  | 'FROZEN'
  | 'LOCKED'
  | 'CANCELED'
  | 'FAILED'

export type Money = {
  amountMinor: number
  currency: string
}

export const CARD_DETAIL_HERO_DEMO = {
  /** Mirrors GET /physical-cards/{id}/billing-address — scope CARD_ACCOUNT. */
  billing: {
    address: {
      line1: '120 Broadway',
      line2: 'Suite 400',
      locality: 'New York',
      region: 'NY',
      postalCode: '10001',
      countryCode: 'US',
    },
  },

  /**
   * Slice 2 reveal values. These have NO contract counterpart by design (see the
   * file header). They are non-Luhn-valid synthetic strings kept solely so the
   * slice-2 reveal UI has something to render behind a fail-closed flag.
   *
   * The card's expiry is NOT here any more — it is a real field on the credit
   * card detail response and is carried on the `card` group instead.
   */
  credentials: {
    pan: '5412 0000 0000 5678',
    cvc: '567',
  },
} as const
